import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { User, LogOut, TrendingUp, CalendarDays, FolderOpen } from "lucide-react";
import { useAgentAuth } from "../../contexts/AgentAuthContext";
import AgentCalendar from "./AgentCalendar";
import UpcomingEducation from "./UpcomingEducation";
import ResourcesSection from "./ResourcesSection";
import TodayCard from "./TodayCard";
import AgentResourceDetail from "./AgentResourceDetail";
import { agentResources } from "../../utils/api";
import DailyNumbersLeaderboard from "../DailyNumbersLeaderboard";
import "../../styles/agent-mobile.css";
import "../../styles/task-mobile.css";

const VALID_TABS = ["overview", "calendar", "resources"];

const AgentDashboard = () => {
  const { agent, logout } = useAgentAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize tab from URL parameter, defaulting to "overview"
  const initialTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    VALID_TABS.includes(initialTab) ? initialTab : "overview"
  );
  const [selectedResource, setSelectedResource] = useState(null); // Track selected resource for detail view
  const [resourceLoading, setResourceLoading] = useState(false); // Track resource loading state
  const [leaderboardRefreshKey, setLeaderboardRefreshKey] = useState(0);

  const handleLogout = async () => {
    await logout();
  };

  // Load a specific resource by ID (for deep linking)
  const loadResourceFromUrl = useCallback(async (resourceId) => {
    try {
      setResourceLoading(true);
      const response = await agentResources.agent.getById(resourceId);
      if (response.data?.success && response.data?.data) {
        setSelectedResource(response.data.data);
        setActiveTab("resources");
      }
    } catch (error) {
      console.error("Failed to load resource from URL:", error);
      // Clear the invalid resource param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("resource");
      setSearchParams(newParams, { replace: true });
    } finally {
      setResourceLoading(false);
    }
  }, [searchParams, setSearchParams]);

  // Deep link: Load resource from URL on mount
  useEffect(() => {
    const resourceId = searchParams.get("resource");
    if (resourceId && !selectedResource) {
      loadResourceFromUrl(resourceId);
    }
  }, [loadResourceFromUrl, searchParams, selectedResource]);

  // Update URL when tab or resource changes
  const updateUrlParams = useCallback((tab, resource) => {
    const newParams = new URLSearchParams();
    if (tab && tab !== "overview") {
      newParams.set("tab", tab);
    }
    if (resource?._id) {
      newParams.set("resource", resource._id);
    }
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  // Sync URL when activeTab changes
  useEffect(() => {
    updateUrlParams(activeTab, selectedResource);
  }, [activeTab, selectedResource, updateUrlParams]);

  // Handler for changing tabs (clears resource selection when leaving resources)
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    if (tabId !== "resources") {
      setSelectedResource(null);
    }
  }, []);

  const tabs = [
    { id: "overview", label: "Dashboard", icon: TrendingUp },
    { id: "calendar", label: "Classes", icon: CalendarDays },
    { id: "resources", label: "Resources", icon: FolderOpen },
  ];

  return (
    <>
        <div
          id="agent-dashboard"
          className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50"
        >
          {/* Header */}
          <header
            id="agent-header"
            className="bg-white shadow-sm border-b border-gray-200"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16">
                <div id="agent-branding" className="flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">
                      Agent Portal
                    </h1>
                    <p className="text-xs text-gray-500">
                      Productivity Coaching
                    </p>
                  </div>
                </div>

                <div id="agent-actions" className="flex items-center space-x-4">
                  <div id="agent-info" className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {agent?.name || "Agent"}
                    </p>
                    <p className="text-xs text-gray-500">{agent?.email}</p>
                    <Link
                      id="agent-email-settings-link"
                      to="/agent/email"
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      Email settings
                    </Link>
                  </div>
                  <button
                    id="agent-logout-button"
                    onClick={handleLogout}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Logout"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </header>

          {/* Navigation Tabs */}
          <nav
            id="agent-navigation"
            className="bg-white border-b border-gray-200"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      id={`tab-${tab.id}`}
                      onClick={() => handleTabChange(tab.id)}
                      className={`
                      relative flex items-center px-1 py-4 text-sm font-medium border-b-2 transition-colors
                      ${
                        isActive
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }
                    `}
                      aria-label={tab.label}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* Main Content */}
          <main
            id="agent-main"
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
          >
            {/* Tab Content */}
            {activeTab === "overview" && (
              <div id="overview-content">
                {/* Welcome Section - Compact */}
                <div id="agent-welcome" className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Welcome back, {agent?.name || "Agent"}!
                      </h2>
                      <p className="text-gray-600">
                        Report yesterday's numbers, register for upcoming classes,
                        and see how the group is doing.
                      </p>
                </div>

                <section
                  id="agent-accountability-home"
                  className="mb-8 grid items-start gap-6 lg:grid-cols-5"
                >
                  <section
                    id="agent-daily-reporting"
                    className="min-w-0 lg:col-span-3"
                  >
                    <TodayCard
                      onSaved={() =>
                        setLeaderboardRefreshKey((current) => current + 1)
                      }
                    />
                  </section>
                  <section
                    id="agent-classes-this-week"
                    className="min-w-0 lg:col-span-2"
                  >
                    <UpcomingEducation
                      onViewAllClasses={() => setActiveTab("calendar")}
                    />
                  </section>
                </section>

                <DailyNumbersLeaderboard
                  audience="agent"
                  refreshKey={leaderboardRefreshKey}
                />

                <section
                  id="agent-resources-preview"
                  className="mt-8 max-w-2xl"
                >
                  <ResourcesSection
                    limit={5}
                    onViewAll={() => setActiveTab("resources")}
                    onResourceClick={(resource) => {
                      setSelectedResource(resource);
                      setActiveTab("resources");
                    }}
                  />
                </section>
              </div>
            )}

            {/* Calendar Tab Content */}
            {activeTab === "calendar" && (
              <div id="calendar-content">
                <AgentCalendar />
              </div>
            )}

            {/* Resources Tab Content */}
            {activeTab === "resources" && (
              <div id="resources-content">
                {resourceLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading resource...</p>
                  </div>
                ) : selectedResource ? (
                  <AgentResourceDetail
                    resource={selectedResource}
                    onBack={() => setSelectedResource(null)}
                  />
                ) : (
                  <>
                    <div className="mb-6">
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Resources
                      </h2>
                      <p className="text-gray-600">
                        Helpful materials and tools to support your success.
                      </p>
                    </div>
                    <ResourcesSection
                      showHeader={false}
                      onResourceClick={(resource) =>
                        setSelectedResource(resource)
                      }
                    />
                  </>
                )}
              </div>
            )}
          </main>

          {/* Footer */}
          <footer
            id="agent-footer"
            className="bg-white border-t border-gray-200 mt-12"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  Productivity Coaching Agent Portal • Secure Magic Link
                  Authentication
                </p>
              </div>
            </div>
          </footer>
        </div>
    </>
  );
};

export default AgentDashboard;
